import * as aiService from '@/services/ai.service';
import type { CompileError, CompileResult } from '@/types';
import { loader } from '@monaco-editor/react';
import { useEffect, useRef } from 'react';

const SECURITY_MARKER_SOURCE = 'security-scan';

/**
 * Push compile errors/warnings as Monaco editor markers (squiggly underlines).
 * Maps CompileError.sourceLocation to line/column positions.
 * Falls back to line 1 when source location is unavailable.
 *
 * After a successful compile, also runs a background security scan and adds
 * any high/critical findings as Warning markers with source='security-scan'.
 */
export function useCompileDiagnostics(
  compileResult: CompileResult | null,
  activeFilePath: string | null,
  currentCode?: string,
) {
  const monacoRef = useRef<typeof import('monaco-editor') | null>(null);

  // Get Monaco instance
  useEffect(() => {
    loader.init().then((monaco) => {
      monacoRef.current = monaco;
    });
  }, []);

  // Set markers when compile result changes
  useEffect(() => {
    const monaco = monacoRef.current;
    if (!monaco) return;

    // Clear all compile markers first
    const allModels = monaco.editor.getModels();
    for (const model of allModels) {
      monaco.editor.setModelMarkers(model, 'solc', []);
    }

    if (!compileResult) return;

    const markers: import('monaco-editor').editor.IMarkerData[] = [];

    const processErrors = (
      errors: CompileError[],
      severity: import('monaco-editor').MarkerSeverity,
    ) => {
      for (const err of errors) {
        // Try to parse line number from message (common format: ":line:col:")
        let startLine = 1;
        let startCol = 1;
        let endLine = 1;
        let endCol = 1000;

        if (err.sourceLocation) {
          // sourceLocation gives byte offsets — we'll use them as rough line estimates
          // In practice the workspace-service should provide line numbers
          // For now, try to parse from message
          const lineMatch = err.message.match(/:(\d+):(\d+):/);
          if (lineMatch) {
            startLine = parseInt(lineMatch[1], 10);
            startCol = parseInt(lineMatch[2], 10);
            endLine = startLine;
            endCol = startCol + 20;
          }
        } else {
          // Try common solc error format: "fileName:line:col:"
          const lineMatch = err.message.match(/(?:^|\s)(\d+):(\d+):/);
          if (lineMatch) {
            startLine = parseInt(lineMatch[1], 10);
            startCol = parseInt(lineMatch[2], 10);
            endLine = startLine;
            endCol = startCol + 20;
          }
        }

        markers.push({
          severity,
          message: cleanMessage(err.message),
          startLineNumber: Math.max(1, startLine),
          startColumn: Math.max(1, startCol),
          endLineNumber: Math.max(1, endLine),
          endColumn: Math.max(1, endCol),
          source: 'solc',
        });
      }
    };

    processErrors(compileResult.errors ?? [], monaco.MarkerSeverity.Error);
    processErrors(compileResult.warnings ?? [], monaco.MarkerSeverity.Warning);

    // Apply markers to the active model (or the first .sol model)
    if (markers.length > 0) {
      let targetModel = activeFilePath
        ? allModels.find((m) => m.uri.path.endsWith(activeFilePath))
        : null;

      if (!targetModel) {
        targetModel = allModels.find((m) => m.uri.path.endsWith('.sol'));
      }

      if (targetModel) {
        monaco.editor.setModelMarkers(targetModel, 'solc', markers);
      }
    }
  }, [compileResult, activeFilePath]);

  // After a successful compile, run a background security scan and surface
  // high/critical findings as Monaco Warning markers.
  useEffect(() => {
    if (!compileResult?.success || !currentCode || !activeFilePath) return;
    // Only scan Solidity files
    if (!activeFilePath.endsWith('.sol')) return;

    const monaco = monacoRef.current;
    if (!monaco) return;

    aiService
      .audit(currentCode.slice(0, 20_000))
      .then((res) => {
        const scanMarkers: import('monaco-editor').editor.IMarkerData[] = [];

        for (const finding of res.findings ?? []) {
          if (finding.severity !== 'high' && finding.severity !== 'critical') continue;

          // Try to parse line from location string e.g. "contract.sol:42:5"
          let line = 1;
          let col = 1;
          if (finding.location) {
            const m = finding.location.match(/:(\d+)(?::(\d+))?/);
            if (m) {
              line = parseInt(m[1], 10);
              col = m[2] ? parseInt(m[2], 10) : 1;
            }
          }

          scanMarkers.push({
            severity:
              finding.severity === 'critical'
                ? monaco.MarkerSeverity.Error
                : monaco.MarkerSeverity.Warning,
            message: `[Security] ${finding.title}: ${finding.description}${finding.recommendation ? '\n\nFix: ' + finding.recommendation : ''}`,
            startLineNumber: Math.max(1, line),
            startColumn: Math.max(1, col),
            endLineNumber: Math.max(1, line),
            endColumn: Math.max(1, col + 30),
            source: SECURITY_MARKER_SOURCE,
          });
        }

        if (scanMarkers.length > 0 && monacoRef.current) {
          const allMdls = monacoRef.current.editor.getModels();
          const target = allMdls.find((m) => m.uri.path.endsWith(activeFilePath));
          if (target) {
            monacoRef.current.editor.setModelMarkers(target, SECURITY_MARKER_SOURCE, scanMarkers);
          }
        }
      })
      .catch(() => {
        // Security scan is best-effort — never block the IDE
      });
  }, [compileResult, activeFilePath, currentCode]);
}

/** Strip file path prefix from error messages for cleaner display */
function cleanMessage(msg: string): string {
  // Remove common solc path prefixes
  return msg.replace(/^.*\.sol:\d+:\d+:\s*/, '').trim() || msg;
}
